import { Fragment } from 'react';
import { Listbox as HeadlessListbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import PremiumIconCrown from './PremiumIconCrown';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}
const OPEN_UP_CLASSES = 'bottom-[50px]';

const getButtonClasses = (disabled, color, buttonClassName) => {
  const defaultClasses =
    'w-full border border-gray-400 rounded-md px-3 sm:h-[40px] h-[50px] py-2 text-gray-900 text-sm font-normal leading-normal focus:border-emerald-500 focus:ring-emerald-500 pac-target-input ' +
    getFocusClass(color) +
    buttonClassName;
  if (disabled) {
    return defaultClasses + 'text-gray-300 pointer-events-none' + buttonClassName;
  } else {
    return defaultClasses + ' bg-white text-gray-800 ' + buttonClassName;
  }
};

const getFocusClass = (colorTheme) => {
  if (colorTheme === 'purple') {
    return 'focus:ring-purple-500 focus:border-purple-500 focus-visible:outline-purple-500';
  } else {
    return 'focus:ring-emerald-500 focus:border-emerald-500 focus-visible:outline-emerald-500';
  }
};
const getActiveBgClass = (colorTheme) => {
  if (colorTheme === 'purple') {
    return 'bg-purple-600';
  } else {
    return 'bg-emerald-600';
  }
};

const getActiveTextDescriptionClass = (colorTheme) => {
  if (colorTheme === 'purple') {
    return 'text-purple-200';
  } else {
    return 'text-emerald-200';
  }
};

const getSelectedTextClass = (colorTheme) => {
  if (colorTheme === 'purple') {
    return 'text-purple-600';
  } else {
    return 'text-emerald-600';
  }
};

export const Listbox = ({
  options, // string[] |  [ { name: string, value: any, description?: string }]
  selected, // string | { name: string, value: any, description?: string }
  setSelected, // (value: any) => void
  id, // string — put on the button, so an external <label htmlFor> actually reaches a control
  label = false, // string | false
  disabled = false,
  openDirection = 'down', // "up" | "down"
  icon = 'ChevronDownIcon',
  placeholder = 'Select an option',
  colorTheme = 'emerald',
  buttonClassName = '',
  textClassName = '',
}) => {
  if (selected && typeof selected === 'string') {
    selected = { name: selected, value: selected };
  }
  if (typeof options[0] === 'string') {
    options = options.map((option) => ({ name: option, value: option }));
  }

  return (
    <HeadlessListbox name={name} value={selected?.value} onChange={setSelected} disabled={disabled}>
      {({ open }) => {
        return (
          <div
            className={
              disabled
                ? 'text-gray-400'
                : 'text-gray-900 focus:border-emerald-500 focus:ring-emerald-500'
            }>
            {label && (
              <HeadlessListbox.Label className={'text-sm text-gray-600 font-semibold'}>
                {label}
              </HeadlessListbox.Label>
            )}
            <div className='relative'>
              <HeadlessListbox.Button
                id={id}
                className={getButtonClasses(disabled, colorTheme, buttonClassName)}>
                <div className='flex flex-row items-center justify-between '>
                  <span
                    className={`text-left w-full leading-normal overflow-hidden text-ellipsis line-clamp-1 ${textClassName}`}>
                    {selected?.name ? (
                      <span>{selected.name}</span>
                    ) : (
                      <span className={disabled ? 'text-gray-300' : ' text-gray-400'}>
                        {placeholder}
                      </span>
                    )}
                  </span>
                  <span className='pointer-events-none flex items-center pl-2'>
                    {/* render the icon passed in  */}
                    {icon === 'ChevronDownIcon' ? (
                      <ChevronDownIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
                    ) : (
                      <ChevronUpDownIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
                    )}
                  </span>
                </div>
              </HeadlessListbox.Button>

              <Transition
                show={open}
                as={Fragment}
                leave='transition ease-in duration-100'
                leaveFrom='opacity-100'
                leaveTo='opacity-0'>
                <HeadlessListbox.Options
                  className={`float-left absolute ${
                    openDirection === 'up' ? OPEN_UP_CLASSES : ''
                  } z-50 mt-1 max-h-40 2xl:max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm`}>
                  {options.map((option, i) => {
                    if (!option.value || !option.name) {
                      return null;
                    }
                    return (
                      <HeadlessListbox.Option
                        key={option.name + i}
                        className={({ active }) =>
                          classNames(
                            active ? getActiveBgClass(colorTheme) + ' text-white' : 'text-gray-500',
                            'relative cursor-default select-none py-2 pl-3 pr-9'
                          )
                        }
                        value={option.value}>
                        {({ selected, active }) => (
                          <>
                            <div className='flex'>
                              <span
                                className={classNames(
                                  selected ? 'font-semibold' : 'font-normal',
                                  'flex items-center gap-1'
                                )}>
                                {option.name}
                                {option.premium && (
                                  <PremiumIconCrown
                                    className='h-3 w-3'
                                    toolTipProps={{
                                      text: '',
                                    }}
                                  />
                                )}
                              </span>
                              {option.description && (
                                <span
                                  className={classNames(
                                    active
                                      ? getActiveTextDescriptionClass(colorTheme)
                                      : 'text-gray-400 text-light',
                                    'ml-2'
                                  )}>
                                  {option.description}
                                </span>
                              )}
                            </div>

                            {selected ? (
                              <span
                                className={classNames(
                                  active ? 'text-white' : getSelectedTextClass(colorTheme),
                                  'absolute inset-y-0 right-0 flex items-center pr-4'
                                )}>
                                <CheckIcon className='h-5 w-5' aria-hidden='true' />
                              </span>
                            ) : null}
                          </>
                        )}
                      </HeadlessListbox.Option>
                    );
                  })}
                </HeadlessListbox.Options>
              </Transition>
            </div>
          </div>
        );
      }}
    </HeadlessListbox>
  );
};
